import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { Organization, OrganizationFormData, formatErrorMessage } from '../types';
import { useStrictModeGuard } from '../hooks/useStrictModeGuard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';

const Organizations: React.FC = () => {
  const { executeOnce } = useStrictModeGuard();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    description: ''
  });

  const fetchOrganizations = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await axios.get('/organizations', {
        params: { skip, limit: itemsPerPage }
      });
      const responseData = response.data;
      const organizationsData = responseData.items || responseData; // Handle both old and new format
      
      setOrganizations(organizationsData);
      
      // Update pagination info from response
      if (responseData.total_count !== undefined) {
        setTotalItems(responseData.total_count);
        setTotalPages(responseData.total_pages);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    executeOnce(() => {
      fetchOrganizations();
    });
  }, [fetchOrganizations, executeOnce]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      if (editingOrg) {
        await axios.put<Organization>(`/organizations/${editingOrg.id}`, formData);
      } else {
        await axios.post<Organization>('/organizations', formData);
      }

      await fetchOrganizations();
      resetForm();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this organization?')) {
      return;
    }

    try {
      await axios.delete(`/organizations/${id}`);
      await fetchOrganizations();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handleEdit = (org: Organization): void => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      description: org.description || ''
    });
    setShowCreateForm(true);
  };

  const resetForm = (): void => {
    setFormData({ name: '', description: '' });
    setShowCreateForm(false);
    setEditingOrg(null);
    setError(null);
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626' }}>Loading organizations...</div>;
  if (error) return <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '24px' }}>Organization Management</h2>

      {/* Add/Edit Organization Form */}
      {showCreateForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '500', color: '#374151', marginBottom: '16px' }}>{editingOrg ? 'Edit Organization' : 'Add New Organization'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">Organization Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="form-input"
                style={{ resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                type="submit"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#16a34a',
                  border: '2px solid white',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  minWidth: '180px',
                  minHeight: '40px'
                }}
              >
                <div style={{
                  backgroundColor: '#16a34a',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <FontAwesomeIcon icon={editingOrg ? faSave : faPlus} style={{ fontSize: '14px' }} />
                </div>
                <div style={{
                  padding: '10px 12px',
                  flex: 1,
                  textAlign: 'center'
                }}>
                  {editingOrg ? 'UPDATE ORGANIZATION' : 'ADD ORGANIZATION'}
                </div>
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#6b7280',
                  border: '2px solid white',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  minWidth: '100px',
                  minHeight: '40px'
                }}
              >
                <div style={{
                  padding: '10px 12px',
                  flex: 1,
                  textAlign: 'center'
                }}>
                  CANCEL
                </div>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Organization Button */}
      {!showCreateForm && (
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#2563eb',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              padding: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              cursor: 'pointer',
              overflow: 'hidden',
              minWidth: '200px',
              minHeight: '40px'
            }}
          >
            <div style={{
              backgroundColor: '#2563eb',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)'
            }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '14px' }} />
            </div>
            <div style={{
              padding: '10px 12px',
              flex: 1,
              textAlign: 'center'
            }}>
              ADD NEW ORGANIZATION
            </div>
          </button>
        </div>
      )}

      {/* Organizations List */}
      <div className="card">
        <h3 style={{ fontSize: '1.25rem', fontWeight: '500', color: '#374151', marginBottom: '16px' }}>All Organizations</h3>
        {(organizations || []).length === 0 ? (
          <p style={{ color: '#6b7280' }}>No organizations found.</p>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {(organizations || []).map((org) => (
              <div
                key={org.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                      {org.name}
                    </h4>
                    {org.description && (
                      <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '14px' }}>
                        {org.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                      <span>Status: {org.is_active ? 'Active' : 'Inactive'}</span>
                      <span>Users: {org.user_count || 0}</span>
                      <span>Applications: {org.application_count || 0}</span>
                      <span>Created: {new Date(org.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit(org)}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f59e0b',
                        border: '2px solid white',
                        borderRadius: '6px',
                        color: 'white',
                        padding: '0',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        minWidth: '80px',
                        minHeight: '32px'
                      }}
                    >
                      <div style={{
                        backgroundColor: '#f59e0b',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: '1px solid rgba(255,255,255,0.3)'
                      }}>
                        <FontAwesomeIcon icon={faEdit} style={{ fontSize: '10px' }} />
                      </div>
                      <div style={{
                        padding: '6px 8px',
                        flex: 1,
                        textAlign: 'center'
                      }}>
                        EDIT
                      </div>
                    </button>
                    <button
                      onClick={() => handleDelete(org.id)}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#dc2626',
                        border: '2px solid white',
                        borderRadius: '6px',
                        color: 'white',
                        padding: '0',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        minWidth: '80px',
                        minHeight: '32px'
                      }}
                    >
                      <div style={{
                        backgroundColor: '#dc2626',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: '1px solid rgba(255,255,255,0.3)'
                      }}>
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                      </div>
                      <div style={{
                        padding: '6px 8px',
                        flex: 1,
                        textAlign: 'center'
                      }}>
                        DELETE
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {organizations.length > 0 && (
          <div className="card">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showInfo={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Organizations;
